import { Button, Grid, Tooltip, Typography } from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@common/Modal/Modal";
import { ICustomService } from "@common/SelectYourNumber/interface/ISubscription";
import "./css/PortInEdit.scss";
import { useSubmitPortIn } from "./hook/useSubmitPortIn";
import { IPortInInformation } from "./interface/IPortIn";
import { useWatch } from "react-hook-form";
import { GET_PORT_IN_SLOT_ROUTE } from "@api-routes/mnpFacade";
import { AccountNumber } from "./components/AccountNumber/AccountNumber";
import { useGetPortinSlot } from "@features/PortIn/hook/useGetPortinSlot";
import { BRAND_NAME } from "@constants";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { ValidatePhoneNumber } from "@features/PortIn/components/ValidatePhoneNumber/ValidatePhoneNumber";
import { CurrentProvider } from "@common/CurrentProvider/CurrentProvider";
import { PortInDate } from "@features/PortIn/components/PortInDate/PortInDate";
import { PortInTimeRange } from "@features/PortIn/components/PortInTimeRange/PortInTimeRange";
import { PaymentType } from "@common/PaymentType/PaymentType";

interface IParams {
    portInDetails?: IPortInInformation;
    previousPhoneNumber: string;
    selectedService: ICustomService;
    closePortIn(): void;
    getPortIn(serviceId: number): void;
}

export const PortInEdit = (props: IParams) => {
    const { closePortIn, portInDetails, previousPhoneNumber, getPortIn, selectedService } = props;

    const { t } = useTranslation(["portin", "common"]);

    const {
        updatePortInState,
        submitPortin,
        formMethods,
        setCurrentProvider,
        setIsFinished,
        currentProvider,
        phoneNumberSelected,
        setPhoneNumberSelected,
        isFinished,
        setIsModalOpen,
        isModalOpen,
    } = useSubmitPortIn(closePortIn, getPortIn, selectedService.id, portInDetails);

    const { getPortinSlotState, portinSlotList, getPortinSlot } = useGetPortinSlot(true, GET_PORT_IN_SLOT_ROUTE);

    const { handleSubmit, control, errors, register } = formMethods;

    const currentProviderName = currentProvider ? currentProvider.networkName : "";

    const onSubmit = handleSubmit(() => {
        setIsModalOpen(true);
    });

    const paymentType = useWatch<string>({ name: "paymentType", control: formMethods.control, defaultValue: "" });
    const isPostPay = paymentType.includes("BILL");

    return (
        <Fragment>
            <Modal
                close={() => setIsModalOpen(false)}
                confirm={submitPortin}
                displayCancel
                isModalOpen={isModalOpen}
                title={t("portin:youWantMoveNumber")}
            >
                <Typography align="center" variant="normal">
                    {t("portin:replacePreviousNumberModal", {
                        brand: BRAND_NAME,
                        previousNumber: selectedService.name,
                    })}
                </Typography>
            </Modal>
            <form onSubmit={onSubmit}>
                <HandleErrorAndLoading
                    error={getPortinSlotState.error}
                    isLoading={getPortinSlotState.isLoading}
                    retryFetch={getPortinSlot}
                >
                    <Typography align="center" gutterBottom variant="normal">
                        {t("portin:scheduleMove")}
                        <br />
                        {t("portin:replacePreviousNumber", {
                            brand: BRAND_NAME,
                            previousNumber: selectedService.name,
                        })}
                    </Typography>
                    <section>
                        <Grid container spacing={2}>
                            <Grid item md={10} xs={12}>
                                <ValidatePhoneNumber
                                    formMethods={formMethods}
                                    phoneNumberSelected={phoneNumberSelected}
                                    previousPhoneNumber={previousPhoneNumber}
                                    setCurrentProvider={setCurrentProvider}
                                    setIsFinished={() => setIsFinished(true)}
                                    setPhoneNumberSelected={setPhoneNumberSelected}
                                    variantStyleButton="contained"
                                />
                            </Grid>
                            <Grid item>
                                {isFinished && currentProviderName && (
                                    <CurrentProvider currentProviderName={currentProviderName} />
                                )}
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <PortInDate formMethod={formMethods} fullWidth portinSlotList={portinSlotList} />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <PortInTimeRange formMethods={formMethods} portinSlotList={portinSlotList} />
                            </Grid>
                            <Grid item xs={12}>
                                {isFinished && (
                                    <Fragment>
                                        <PaymentType control={control} error={errors.paymentType} />
                                        <Tooltip
                                            color="secondary"
                                            disableFocusListener
                                            disableTouchListener
                                            placement="top-end"
                                            title={t("portin:portinPaymentType")}
                                        >
                                            <HelpIcon />
                                        </Tooltip>
                                    </Fragment>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                {isFinished && isPostPay && (
                                    <AccountNumber error={errors.accountNumber} register={register} />
                                )}
                            </Grid>
                        </Grid>
                    </section>
                    {updatePortInState.error && (
                        <Typography color="error">
                            {updatePortInState.error.errorMessage || t("common:genericError")}
                        </Typography>
                    )}
                    <Grid container justifyContent="flex-end" spacing={2}>
                        <Grid item md={2} sm={3} xs={4}>
                            <Button
                                color="secondary"
                                disabled={updatePortInState.isLoading}
                                fullWidth
                                id="cancelButton"
                                size="large"
                                variant="outlined"
                                onClick={closePortIn}
                            >
                                {t("common:cancel")}
                            </Button>
                        </Grid>
                        <Grid item md={2} sm={3} xs={4}>
                            <Button
                                color="secondary"
                                disabled={updatePortInState.isLoading || !isFinished}
                                fullWidth
                                id="submitButton"
                                size="large"
                                type="submit"
                                variant="contained"
                            >
                                {t("common:confirm")}
                            </Button>
                        </Grid>
                    </Grid>
                </HandleErrorAndLoading>
            </form>
        </Fragment>
    );
};
